import React from 'react';
import type { PatientRecall, PatientRecord } from '../../../patients/types';
import { Icon } from '../../Icon/Icon';
import { Pill } from '../../Pill/Pill';
import { PatientProfileSectionCard } from '../PatientProfileSectionCard';

export interface PatientNextVisitCardProps {
  patient: PatientRecord;
}

const RECALL_TONE: Record<PatientRecall['status'], { fg: string; label: string }> = {
  overdue: { fg: 'var(--terracotta)', label: 'Recall overdue' },
  due: { fg: '#9A6B1E', label: 'Recall due' },
  scheduled: { fg: 'var(--success)', label: 'Recall booked' },
  unknown: { fg: 'var(--ink-4)', label: 'Recall' },
};

/**
 * Next appointment and recall together — they answer the same question ("when
 * do we see this patient next?") and the front desk acts on them in the same
 * breath.
 *
 * v1 showed the family's overdue recalls but never this patient's own, which is
 * the most common booking lever on the page.
 */
export function PatientNextVisitCard({ patient: p }: PatientNextVisitCardProps) {
  const recall = p.recall;
  const tone = recall ? RECALL_TONE[recall.status] : null;

  return (
    <PatientProfileSectionCard icon="calendar" title="Next appointment" accent="var(--ai-3)">
      <div className="font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.01em] text-[var(--ink-0)]">
        {p.appt.when}
      </div>
      <div className="mt-1 text-[13px] text-[var(--ink-2)]">{p.appt.proc}</div>
      <div className="mt-2.5 flex flex-wrap gap-2">
        <Pill tone="neutral">{p.appt.prov}</Pill>
        {p.appt.op !== '—' ? <Pill tone="ai">{p.appt.op}</Pill> : null}
      </div>

      {recall && tone ? (
        <div className="mt-3 flex items-center gap-2 border-t border-[var(--cream-2)] pt-3">
          <Icon kind="refresh" size={13} stroke={2} style={{ color: tone.fg }} />
          <span className="text-[12.5px] text-[var(--ink-3)]">{tone.label}</span>
          <span className="ml-auto text-[12.5px] font-semibold" style={{ color: tone.fg }}>
            {recall.detail}
          </span>
        </div>
      ) : null}
    </PatientProfileSectionCard>
  );
}
